/**
 * Tools of the townsfolk's trades, held only in the poses that use them: a fishing rod with its
 * line, a hoe, a broom, a shoulder pole with two baskets, a net across the lap, a cup; and for the life acts a
 * bowl, a bun, a sickle, an axe, a casting net, a wok, a wooden sword and incense. Like a
 * weapon, a tool attaches to the hand joints, so it follows the pose in every camera view.
 */
import { type Layer, type Piece, px, segment } from './figure';
import { GROUND, type Skeleton } from './skeleton';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

const WOOD = '#8a5a32';
const WOOD_S = '#5e3a1e';
const BAMBOO = '#c8b070';
const BAMBOO_S = '#8a7440';
const IRON = '#7a7e88';
const IRON_H = '#b8bdc8';
const STRAW = '#d8b86a';
const STRAW_S = '#a8884a';

/** One-pixel line. */
function line(g: G, a: Pt, b: Pt, c: string): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
  for (let i = 0; i <= n; i++) px(g, a.x + ((b.x - a.x) * i) / n, a.y + ((b.y - a.y) * i) / n, 1, 1, c);
}

/** Which way "ahead" is on screen: along the facing when turned, out to the tool hand's side head-on. */
function ahead(sk: Skeleton): number {
  if (!sk.square) return 1;
  return sk.joints.handA.x >= sk.joints.chest.x ? 1 : -1;
}

/** Unit vector from the shoulder through the hand: the way a held shaft points. */
function along(sk: Skeleton): Pt {
  const { shoulderA: s, handA: h } = sk.joints;
  const d = Math.max(0.001, Math.hypot(h.x - s.x, h.y - s.y));
  return { x: (h.x - s.x) / d, y: (h.y - s.y) / d };
}

/** A shaft from `back` tiles behind the hand to `fwd` ahead of it, stopping at the ground. */
function shaft(sk: Skeleton, dir: Pt, back: number, fwd: number): [Pt, Pt] {
  const h = sk.joints.handA;
  const a = { x: h.x - dir.x * back, y: h.y - dir.y * back };
  let len = fwd;
  if (dir.y > 0) len = Math.min(len, (GROUND - 1 - h.y) / dir.y);
  return [a, { x: h.x + dir.x * len, y: h.y + dir.y * len }];
}

function rod(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  const f = ahead(sk);
  const tip = { x: h.x + f * 15, y: h.y - 17 };
  segment(g, { x: h.x - f * 3, y: h.y + 3 }, h, 2, 2, BAMBOO, BAMBOO_S);
  line(g, h, tip, BAMBOO);
  line(g, { x: h.x, y: h.y + 1 }, { x: tip.x, y: tip.y + 1 }, BAMBOO_S);
  // The line drops from the tip down to the water, a red float on it.
  const end = GROUND - 2;
  for (let y = tip.y + 1; y < end; y += 1)
    px(g, tip.x + Math.sin(y * 0.3) * 0.4, y, 1, 1, 'rgba(235,235,240,0.75)');
  px(g, tip.x - 1, end, 3, 2, '#d8443a');
  px(g, tip.x - 1, end, 3, 1, '#ffffff');
}

function hoe(g: G, sk: Skeleton): void {
  const dir = along(sk);
  const [a, b] = shaft(sk, dir, 5, 13);
  segment(g, a, b, 2, 2, WOOD, WOOD_S);
  // The blade at the end, bent forward from the handle.
  const f = ahead(sk);
  const n = { x: -dir.y * f, y: dir.x * f };
  const bend = n.y < 0 ? { x: -n.x, y: -n.y } : n;
  const tip = { x: b.x + bend.x * 4 + dir.x, y: Math.min(GROUND - 1, b.y + bend.y * 4 + dir.y) };
  segment(g, b, tip, 3, 2, IRON, IRON);
  line(g, b, tip, IRON_H);
}

function broom(g: G, sk: Skeleton): void {
  const dir = along(sk);
  const [a, b] = shaft(sk, dir, 6, 30);
  segment(g, a, b, 2, 2, BAMBOO, BAMBOO_S);
  // The bristles fan out over the ground.
  for (let k = -3; k <= 3; k++) {
    const end = { x: b.x + k * 1.3 + dir.x * 2, y: GROUND - 1 };
    line(g, { x: b.x + k * 0.4, y: b.y - 1 }, end, k % 2 ? STRAW_S : STRAW);
  }
}

function pole(g: G, sk: Skeleton): void {
  const { shoulderA, shoulderB, chest } = sk.joints;
  const y = Math.min(shoulderA.y, shoulderB.y) - 2;
  const half = sk.square ? 13 : 16;
  const l = { x: chest.x - half, y };
  const r = { x: chest.x + half, y: y + (sk.square ? 0 : 1) };
  segment(g, l, r, 2, 2, BAMBOO, BAMBOO_S);
  for (const end of [l, r]) {
    // Ropes down to a basket heaped with greens.
    line(g, { x: end.x - 2, y: end.y + 1 }, { x: end.x - 3, y: end.y + 8 }, '#6a5034');
    line(g, { x: end.x + 2, y: end.y + 1 }, { x: end.x + 3, y: end.y + 8 }, '#6a5034');
    px(g, end.x - 4, end.y + 6, 9, 2, '#5aa048');
    px(g, end.x - 3, end.y + 5, 5, 1, '#7ac060');
    px(g, end.x - 4, end.y + 8, 9, 5, STRAW);
    px(g, end.x - 4, end.y + 10, 9, 1, STRAW_S);
    px(g, end.x - 3, end.y + 13, 7, 1, STRAW_S);
  }
}

function net(g: G, sk: Skeleton): void {
  const { pelvis } = sk.joints;
  const x0 = Math.round(pelvis.x - 9);
  const y0 = GROUND - 7;
  for (let y = 0; y < 6; y++)
    for (let x = 0; x < 20; x++)
      if ((x + y) % 3 === 0 || (x - y + 30) % 3 === 0)
        px(g, x0 + x, y0 + y, 1, 1, y === 5 ? '#a8a088' : '#d8d0b4');
  // Floats along the edge.
  for (let x = 1; x < 20; x += 6) px(g, x0 + x, y0 + 5, 2, 1, '#d8443a');
}

function cup(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  px(g, h.x - 1, h.y - 2, 3, 3, '#ece6d6');
  px(g, h.x - 1, h.y, 3, 1, '#b8ae98');
  px(g, h.x - 1, h.y - 2, 3, 1, '#5a8aa8');
}

// ── the townsfolk's daily life (lane C0-NPC: render/motions/life.ts poses) ──

/** A rice bowl in the hand, a pair of chopsticks standing out of it. */
function bowl(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  px(g, h.x - 2, h.y - 2, 5, 2, '#ece6d6');
  px(g, h.x - 1, h.y, 3, 1, '#b8ae98');
  px(g, h.x - 2, h.y - 2, 5, 1, '#3e6a8a');
  px(g, h.x - 1, h.y - 3, 3, 1, '#f4f0e4');
  line(g, { x: h.x + 1, y: h.y - 3 }, { x: h.x + 3, y: h.y - 7 }, WOOD);
}

/** A steamed bun. */
function bun(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  px(g, h.x - 1, h.y - 3, 3, 3, '#f4ecd8');
  px(g, h.x - 1, h.y - 1, 3, 1, '#c8b896');
  px(g, h.x, h.y - 3, 1, 1, '#ffffff');
}

/** A sickle: a short handle and a hooked blade ahead of the hand. */
function sickle(g: G, sk: Skeleton): void {
  const dir = along(sk);
  const h = sk.joints.handA;
  const f = ahead(sk);
  const end = { x: h.x + dir.x * 4, y: h.y + dir.y * 4 };
  segment(g, { x: h.x - dir.x * 2, y: h.y - dir.y * 2 }, end, 2, 2, WOOD, WOOD_S);
  // The hook curls forward and back toward the hand.
  const pts: Pt[] = [];
  for (let k = 0; k <= 6; k++) {
    const a = (k / 6) * Math.PI * 0.95;
    pts.push({ x: end.x + f * Math.sin(a) * 4, y: end.y - Math.cos(a) * 3 - 1 });
  }
  for (let k = 0; k < pts.length - 1; k++) line(g, pts[k]!, pts[k + 1]!, k % 2 ? IRON : IRON_H);
}

/** A woodcutter's axe: a long haft, the head at its end. */
function axe(g: G, sk: Skeleton): void {
  const dir = along(sk);
  const [a, b] = shaft(sk, dir, 4, 10);
  segment(g, a, b, 2, 2, WOOD, WOOD_S);
  const f = ahead(sk);
  const n = { x: -dir.y * f, y: dir.x * f };
  const side = n.y < 0 ? { x: -n.x, y: -n.y } : n;
  for (let k = -2; k <= 2; k++) {
    const o = { x: b.x - dir.x * k * 0.7, y: b.y - dir.y * k * 0.7 };
    line(g, o, { x: o.x + side.x * 4, y: o.y + side.y * 4 }, k === -2 ? IRON_H : IRON);
  }
}

/** A casting net: gathered in the hands, flung out in a fan as the arms go wide. */
function castNet(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  const f = ahead(sk);
  const out = sk.joints.handA.y < sk.joints.chest.y;
  const r = out ? 12 : 4;
  for (let k = -4; k <= 4; k++) {
    const a = (k / 4) * 0.9;
    const tip = { x: h.x + f * Math.cos(a) * r, y: h.y + Math.sin(a) * r * 0.6 + (out ? 2 : 3) };
    line(g, h, tip, '#d8d0b4');
    px(g, tip.x, tip.y, 1, 1, '#8a8a8a');
  }
}

/** A wok tossed over the stall's fire, something in it. */
function wok(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  const f = ahead(sk);
  const c = { x: h.x + f * 5, y: h.y + 1 };
  line(g, h, { x: c.x - f * 3, y: c.y - 1 }, WOOD_S);
  px(g, c.x - 4, c.y, 9, 2, '#2e2a2a');
  px(g, c.x - 3, c.y + 2, 7, 1, '#1a1616');
  px(g, c.x - 2, c.y - 1, 5, 1, '#d8a040');
  px(g, c.x - 1, c.y - 2, 2, 1, '#6aa048');
}

/** A child's wooden sword. */
function woodSword(g: G, sk: Skeleton): void {
  const dir = along(sk);
  const [a, b] = shaft(sk, dir, 2, 10);
  segment(g, a, b, 2, 1, '#c89a5a', '#8a6236');
  const h = sk.joints.handA;
  const n = { x: -dir.y, y: dir.x };
  line(g, { x: h.x + n.x * 2 + dir.x, y: h.y + n.y * 2 + dir.y }, { x: h.x - n.x * 2 + dir.x, y: h.y - n.y * 2 + dir.y }, WOOD_S);
}

/** Three sticks of incense held up, smoke curling from the tips. */
function incense(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  for (let k = -1; k <= 1; k++) {
    line(g, { x: h.x + k, y: h.y }, { x: h.x + k * 2, y: h.y - 7 }, '#a8443a');
    px(g, h.x + k * 2, h.y - 8, 1, 1, '#ffb050');
  }
  for (let y = 0; y < 5; y++) px(g, h.x + Math.round(Math.sin(y) * 1.5), h.y - 10 - y, 1, 1, 'rgba(220,220,230,0.5)');
}

/** An old man's walking stick: from the hands down to the ground a little ahead of the feet, a crook at the top. */
function cane(g: G, sk: Skeleton): void {
  const h = sk.joints.handA;
  const f = ahead(sk);
  const foot = { x: h.x + f * (sk.square ? 1 : 2), y: GROUND - 1 };
  segment(g, { x: h.x, y: h.y - 1 }, foot, 2, 2, WOOD, WOOD_S);
  // The crook curls over the hands.
  px(g, h.x - f * 1, h.y - 2, 3, 1, WOOD_S);
  px(g, h.x - f * 2, h.y - 1, 1, 2, WOOD_S);
}

const DRAW: Partial<Record<string, (g: G, sk: Skeleton) => void>> = {
  fish: rod,
  hoe,
  sweep: broom,
  haul: pole,
  mend: net,
  drink: cup,
  lifeSitEat: bowl,
  lifeEatStand: bun,
  lifeHarvest: sickle,
  lifeChop: axe,
  lifeCast: castNet,
  lifeCook: wok,
  lifeMimic: woodSword,
  lifeIncense: incense,
  // Tools an idle names itself (Skeleton.tool, motions/idles.ts).
  cane,
};

/** The tool for the pose being drawn, if it uses one. */
export function toolLayer(): Layer {
  return {
    id: 'tool',
    label: 'เครื่องมือ',
    pieces(sk): Piece[] {
      const draw = DRAW[sk.tool ?? sk.pose];
      if (!draw) return [];
      const h = sk.joints.handA;
      // A pole rests on the shoulders in front of the body; a net lies on the lap; the rest are in hand.
      const z = sk.pose === 'haul' ? 1.2 : sk.pose === 'mend' ? 1.1 : h.z - 0.005;
      return [{ z, draw: (g) => draw(g, sk) }];
    },
  };
}
